import { useEffect, useMemo, useRef, useState } from "react";

import { Contact } from "../models/Contact";
import { getContacts, GetContactsPayload } from "../services/contactServices";
import { useApiProvider } from "../providers/api/apiProvider";
import { useLocation } from "react-router-dom";
import { useSocket } from "../providers/socket/socketProvider";
import { PresenceItem, PresenceSnapshot } from "../models/Presence";

export const useContactController = () => {
  const {
    pathname,
  } = useLocation();

  const {
    paginatedApiCall
  } = useApiProvider();

  const { socket } = useSocket();

  const [ page, setPage ] = useState<number>( 1 );
  const [ perPage, setPerPage ] = useState<number>( 15 );
  const [ total, setTotal ] = useState<number>( 0 );
  const [ search, setSearch ] = useState<string>( "" );

  const [ contacts, setContacts ] = useState<Array<Contact>>([]);
  const [ loadingContacts, setLoadingContacts ] = useState<boolean>( false );

  const [ contact, setContact ] = useState<Contact | null>( null );

  const [ openContactModal, setOpenContactModal ] = useState<boolean>( false );
  const didMountModalEffect = useRef<boolean>( false );

  const handleContact = ( contact: Contact | null ) => {
    setContact( contact );

    if( Boolean( contact )) setOpenContactModal( true );
  };

  const changePage = ( page: number ) => setPage( page );

  const changeSearch = ( value: string ) => {
    setSearch( value );
    setPage( 1 );
  };

  const loadContacts = async () => {
    const response = await paginatedApiCall<GetContactsPayload, Contact>( getContacts, { page, perPage, search: search.trim() || undefined }, () => setLoadingContacts(( value ) => !value ));

    if( response !== null ) {
      setPage( response.page );
      setTotal( response.total );
      setPerPage( response.perPage );

      setContacts( response.results );
    }
  };

  const applyContactPresence = ( presence: PresenceItem ) => {
    if( presence.type !== "contact" ) return;

    setContacts(( contacts ) => contacts.map(( item ) => item.id === presence.id ? { ...item, isOnline: presence.isOnline } : item ));
    setContact(( contact ) => contact?.id === presence.id ? { ...contact, isOnline: presence.isOnline } : contact );
  };

  const handleToggleOpenContactModal = () => setOpenContactModal(( value ) => !value );

  useEffect(() => {
    loadContacts();
    // eslint-disable-next-line
  }, [ page, perPage, pathname, search ]);

  useEffect(() => {
    if( !didMountModalEffect.current ) {
      didMountModalEffect.current = true;
      return;
    }

    if( !openContactModal ) loadContacts().then(() => handleContact( null ));
    // eslint-disable-next-line
  }, [ openContactModal ]);

  useEffect(() => {
    if( !socket ) return;

    const handleSnapshot = ( snapshot: PresenceSnapshot ) => {
      snapshot.contacts?.forEach( applyContactPresence );
    };
    const handleUpdate = ( presence: PresenceItem ) => applyContactPresence( presence );

    socket.on("presence:snapshot", handleSnapshot);
    socket.on("presence:update", handleUpdate);

    return () => {
      socket.off("presence:snapshot", handleSnapshot);
      socket.off("presence:update", handleUpdate);
    };
    // eslint-disable-next-line
  }, [ socket ]);

  return useMemo(() => ({
    page,
    perPage,
    total,
    search,
    contacts,
    loadingContacts,
    contact,
    openContactModal,
    changePage,
    changeSearch,
    handleToggleOpenContactModal,
    handleContact,
    // eslint-disable-next-line
  }), [
    page,
    perPage,
    total,
    search,
    contacts,
    loadingContacts,
    contact,
    openContactModal,
  ]);
};
